import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { differenceInDays } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { SubscriptionStatusAlert } from "@/components/athlete/subscription/SubscriptionStatusAlert";
import { SubscriptionCard } from "@/components/athlete/subscription/SubscriptionCard";
import { BillingHistory } from "@/components/athlete/subscription/BillingHistory";
import { SubscriptionPaymentDialog } from "@/components/athlete/subscription/SubscriptionPaymentDialog";
import { SubscriptionHistoryAccordion } from "@/components/athlete/subscription/SubscriptionHistoryAccordion";
import { useClientSubscriptionStore } from "@/store/api/clients/clientSubscriptionStore";
import { useUserStore } from "@/store/userStore";
import { useSubscriptionCalendarLabels } from "@/hooks/useSubscriptionCalendarLabels";
import type { Subscription } from "@/types/subscription";
import { AthleteBrandHeader } from "@/components/athlete/AthleteBrandHeader";
import { AthleteHeroBackground } from "@/components/athlete/AthleteHeroBackground";
import { useThemeCustomization } from "@/hooks/useThemeCustomization";

const AthleteSubscriptions = () => {
  const { t } = useTranslation(["athlete", "subscriptions", "common"]);
  const { toast } = useToast();

  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [renewTarget, setRenewTarget] = useState<Subscription | null>(null);

  const {isLoading, loadClientSubscriptionData, subscription, billingHistory, allSubscriptions, paymentsBySub} = useClientSubscriptionStore();
  const gymId = useUserStore((s) => s.gymId);
  const { logoDark, brandName, athleteTheme, athleteBackgrounds } = useThemeCustomization();
  const isFeatured = athleteTheme.variant === "dark-premium";

  useEffect(() => {
    const fetchData = async () => {
      try {
        await loadClientSubscriptionData();
      } catch (error) {
        console.error("Error:", error);
        toast({
          title: t("errors.fetchError", { ns: "common" }),
          description: t("errors.tryAgain", { ns: "common" }),
          variant: "destructive",
        });
      }
    };
    fetchData();
    // gymId dans les dépendances : sans lui, un changement de salle laissait
    // affichés les abonnements de la salle précédente.
  }, [toast, t, loadClientSubscriptionData, gymId]);

  // Un adhérent peut détenir PLUSIEURS abonnements actifs, de plannings
  // différents (« Planning général » et « Cours Privés » par exemple), avec des
  // quotas et des dates de fin indépendants. N'en montrer qu'un revenait à lui
  // cacher des séances qu'il a payées.
  const activeSubscriptions = useMemo(
    () => (allSubscriptions ?? []).filter((s) => s.status === "Active"),
    [allSubscriptions]
  );
  const activeIds = useMemo(
    () => new Set(activeSubscriptions.map((s) => s.id)),
    [activeSubscriptions]
  );

  const { calendarLabelFor } = useSubscriptionCalendarLabels(activeSubscriptions, gymId);

  const isExpiringSoonFor = (sub: Subscription) => {
    const daysLeft = differenceInDays(new Date(sub.end_date), new Date());
    return daysLeft <= 30 && daysLeft >= 0;
  };

  const handleRenewSubscription = (sub: Subscription) => {
    setRenewTarget(sub);
    setIsPaymentDialogOpen(true);
  };

  const handleSubscriptionSuccess = () => {
    toast({
      title: t("subscriptions.paymentSuccess", { ns: "athlete" }),
      description: t("subscriptions.renewalSuccess", { ns: "athlete" }),
      duration: 5000,
    });
    setIsPaymentDialogOpen(false);
  };

  // Avec un seul abonnement actif on garde exactement l'historique calculé par
  // le store. Avec plusieurs, on fusionne les paiements de tous les
  // abonnements en cours : sinon l'adhérent ne verrait que ceux du premier.
  const displayedBillingHistory = useMemo(() => {
    if (activeSubscriptions.length <= 1) return billingHistory;
    const paymentsById = (paymentsBySub ?? {}) as Record<string, { date?: string }[]>;
    return activeSubscriptions
      .flatMap((s) => (s.id ? paymentsById[s.id] ?? [] : []))
      .slice()
      .sort((a, b) => String(b?.date ?? "").localeCompare(String(a?.date ?? "")))
      .slice(0, 5);
  }, [activeSubscriptions, billingHistory, paymentsBySub]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center athlete-page-bg">
        <p className="text-muted-foreground">{t("loading", { ns: "common" })}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col w-full athlete-page-bg">
      <main className="flex-1 p-4 md:p-6 max-w-7xl mx-auto w-full pb-24">
        {isFeatured ? (
          <section className="athlete-subscription-hero relative -mx-4 -mt-4 overflow-hidden px-5 pb-8 pt-7 md:mx-0 md:mt-0 md:rounded-md md:px-8">
            <AthleteHeroBackground src={athleteBackgrounds.subscriptions} className="absolute inset-0 h-full w-full object-cover object-center" />
            <div className="athlete-subscription-hero-overlay absolute inset-0" />
            <div className="relative z-10">
              <img src={logoDark} alt={brandName} className="h-9 w-auto max-w-[9rem] object-contain" />
              <p className="mt-8 text-xs font-medium uppercase tracking-[0.28em] text-muted-foreground">{t("subscriptions.eyebrow", { ns: "athlete" })}</p>
              <h1 className="mt-3 text-4xl font-bold md:text-5xl">{t("subscriptions.title", { ns: "athlete" })}</h1>
              <p className="mt-2 max-w-xl text-sm text-muted-foreground md:text-base">{t("subscriptions.subtitle", { ns: "athlete" })}</p>
            </div>
          </section>
        ) : (
          <AthleteBrandHeader title={t("subscriptions.title", { ns: "athlete" })} subtitle={t("subscriptions.subtitle", { ns: "athlete" })} backgroundKey="subscriptions" />
        )}

        {activeSubscriptions.map((sub) => (
          <SubscriptionStatusAlert
            key={`alert-${sub.id}`}
            subscription={sub}
            calendarLabel={calendarLabelFor(sub.id)}
            featured={isFeatured}
          />
        ))}

        <div className="grid gap-6">
          {activeSubscriptions.length > 1 && (
            <h2 className="text-lg font-semibold text-foreground -mb-2">
              {t("activePlans", { ns: "subscriptions", defaultValue: "Abonnements en cours" })}
            </h2>
          )}

          {activeSubscriptions.length > 0 ? (
            activeSubscriptions.map((sub) => (
              <SubscriptionCard
                key={sub.id}
                subscription={sub}
                onRenew={() => handleRenewSubscription(sub)}
                isExpiringSoon={isExpiringSoonFor(sub)}
                calendarLabel={calendarLabelFor(sub.id)}
                featured={isFeatured}
                // billingHistory est tronqué à 5 lignes : le calcul du montant
                // payé et du reste à payer doit porter sur TOUS les paiements
                // de l'abonnement, sinon un règlement en 6 fois affiche un
                // solde restant faux.
                payments={(sub.id ? paymentsBySub?.[sub.id] : undefined) ?? billingHistory}
              />
            ))
          ) : (
            <Card className="bg-card/60 backdrop-blur-xl border border-border/40 shadow-lg">
              <CardContent className="pt-6">
                <p className="text-center text-muted-foreground">
                  {t("subscriptions.noActiveSubscription", { ns: "athlete" })}
                </p>
              </CardContent>
            </Card>
          )}

          <BillingHistory billingHistory={displayedBillingHistory} featured={isFeatured} />

          <SubscriptionHistoryAccordion
            subscriptions={allSubscriptions.filter((s) => !activeIds.has(s.id))}
            paymentsBySubscription={paymentsBySub}
            featured={isFeatured}
          />
        </div>
      </main>

      <SubscriptionPaymentDialog
        open={isPaymentDialogOpen}
        onOpenChange={setIsPaymentDialogOpen}
        subscription={renewTarget ?? subscription}
        onSuccess={handleSubscriptionSuccess}
      />
    </div>
  );
};

export default AthleteSubscriptions;
